import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import BUILD_VERSION from "../../../buildVersion";

const MAX_RELOAD_ATTEMPTS = 3;

async function fetchServerVersion() {
    const response = await fetch(`/version.json?ts=${Date.now()}`, {
        cache: "no-store"
    });

    if (!response.ok) {
        throw new Error(`version.json load failed: ${response.status}`);
    }

    const data = await response.json();
    return String(data?.version || "").trim();
}

async function clearClientCaches() {
    try {
        if ("caches" in window) {
            const keys = await caches.keys();
            await Promise.all(keys.map((key) => caches.delete(key)));
        }
    } catch (e) {
        console.error("Cache clear failed:", e);
    }

    try {
        if ("serviceWorker" in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            await Promise.all(registrations.map((reg) => reg.unregister()));
        }
    } catch (e) {
        console.error("SW unregister failed:", e);
    }
}

export default function useSiteVersionSync() {
    const location = useLocation();
    const isCheckingRef = useRef(false);
    const isReloadingRef = useRef(false);

    const checkVersion = async () => {
        if (isCheckingRef.current || isReloadingRef.current) return;

        isCheckingRef.current = true;

        try {
            const serverVersion = await fetchServerVersion();
            if (!serverVersion) return;

            const reloadTarget = sessionStorage.getItem("site_reload_target") || "";
            const reloadCount = Number(sessionStorage.getItem("site_reload_count") || "0");

            if (serverVersion === BUILD_VERSION) {
                sessionStorage.removeItem("site_reload_target");
                sessionStorage.removeItem("site_reload_count");

                const url = new URL(window.location.href);
                if (url.searchParams.has("__v")) {
                    url.searchParams.delete("__v");
                    window.history.replaceState({}, "", url.toString());
                }

                return;
            }

            if (reloadTarget === serverVersion && reloadCount >= MAX_RELOAD_ATTEMPTS) {
                console.warn(
                    `Version mismatch persists after ${MAX_RELOAD_ATTEMPTS} reload attempts.`,
                    { BUILD_VERSION, serverVersion }
                );
                return;
            }

            isReloadingRef.current = true;

            sessionStorage.setItem("site_reload_target", serverVersion);
            sessionStorage.setItem(
                "site_reload_count",
                String(reloadTarget === serverVersion ? reloadCount + 1 : 1)
            );

            await clearClientCaches();

            const url = new URL(window.location.href);
            url.searchParams.set("__v", serverVersion);

            window.location.replace(url.toString());
        } catch (e) {
            console.error("Version check failed:", e);
        } finally {
            isCheckingRef.current = false;
        }
    };

    useEffect(() => {
        checkVersion();
    }, [location.pathname, location.search]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                checkVersion();
            }
        };

        const handleFocus = () => {
            checkVersion();
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("focus", handleFocus);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("focus", handleFocus);
        };
    }, []);
}